export type CareerTranslation = {
  en: string;
  fr: string;
};

export type Job = {
  id: string;
  slug: string;
  title: CareerTranslation;
  shortDescription: CareerTranslation;
  description: CareerTranslation;
  /** Ce que vous ferez */
  missions: string;
  /** Profil recherché */
  profile: string;
  /** Ce que nous offrons */
  offer: string;
  location: string;
  mapLat?: number;
  mapLng?: number;
  employmentType: string;
  department?: string;
  coverImage?: string;
  isPublished?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type JobApplicationStatus = 'new' | 'reviewed' | 'archived';

export type JobApplication = {
  id: string;
  jobId: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  cvUrl: string;
  extraInfo?: string;
  status: JobApplicationStatus;
  createdAt: string;
  job?: Pick<Job, 'id' | 'slug' | 'title'>;
};

export type CareersPageSettings = {
  id: string;
  heroEyebrowEn?: string;
  heroEyebrowFr?: string;
  heroTitleEn?: string;
  heroTitleFr?: string;
  heroSubtitleEn?: string;
  heroSubtitleFr?: string;
  heroBackgroundImage?: string;
};

/** French-only admin input; EN columns are mirrored from FR for compatibility. */
export type JobInput = {
  title: string;
  shortDescription?: string;
  description: string;
  missions: string;
  profile: string;
  offer: string;
  location: string;
  mapLat?: number;
  mapLng?: number;
  employmentType: string;
  department?: string;
  coverImage?: string;
  isPublished: boolean;
};

export type JobApplicationInput = {
  jobId: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  cvUrl: string;
  extraInfo?: string;
};

/** Job content is French-first; EN falls back to FR. */
export const careerCopy = (
  value: CareerTranslation | undefined,
  _language?: string
): string => {
  if (!value) return '';
  return value.fr || value.en || '';
};

export const toJobSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/** OpenStreetMap embed with a visible marker pin. */
export const mapsEmbedUrl = (lat: number, lng: number, delta = 0.02) => {
  const left = lng - delta;
  const right = lng + delta;
  const top = lat + delta * 0.75;
  const bottom = lat - delta * 0.75;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${lat}%2C${lng}`;
};

export const mapsExternalUrl = (lat: number, lng: number, label?: string) => {
  const q = label?.trim()
    ? encodeURIComponent(label)
    : `${lat},${lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

export const geocodeLocation = async (
  query: string
): Promise<{ lat: number; lng: number; displayName: string } | null> => {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(trimmed)}`;
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) throw new Error('Geocoding failed');
  const results = (await response.json()) as Array<{
    lat: string;
    lon: string;
    display_name: string;
  }>;
  if (!results?.length) return null;
  return {
    lat: Number(results[0].lat),
    lng: Number(results[0].lon),
    displayName: results[0].display_name,
  };
};
